/**
 * The comp-cancellation half of `DELETE /api/v2/bookings/:id`. Its own module
 * because `bookingsV2.ts` is at the line cap, and one retire path per file
 * mirrors how the two services are already split.
 */
import { apiFetch } from '../lib/apiFetch';

/** Separate from `RefundBookingError` so a caller can tell which retire path
 *  refused it without inspecting the code. */
export class CancelBookingError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'CancelBookingError';
  }
}

async function cancelBookingErrorFromResponse(res: Response): Promise<CancelBookingError> {
  const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
  // Code before status: five refusals share 409.
  const byCode: Record<string, string> = {
    ALREADY_CANCELLED: 'This booking has already been cancelled',
    NOT_CONFIRMED: 'Only a confirmed booking can be cancelled',
    WORKSHOP_ALREADY_ENDED: 'This workshop has already taken place',
    NOT_COMP: 'Only a comped booking can be cancelled — refund it instead',
    COMP_HAS_CASH:
      'This booking is marked as a comp but has a payment recorded against it. Refund it instead.',
  };
  const fallback =
    (body.code ? byCode[body.code] : undefined) ??
    (res.status === 409
      ? 'This booking cannot be cancelled'
      : res.status === 404
        ? 'Booking not found'
        : res.status === 403
          ? "You don't have access to this booking's region"
          : res.status === 400
            ? 'Please say why this booking is being cancelled'
            : `Failed to cancel booking: ${res.status}`);
  return new CancelBookingError(body.message || fallback, res.status, body.code);
}

export interface CancelBookingResult {
  bookingId: string;
  status: 'CANCELLED';
  seatsReturned: number;
}

/** The server picks the branch from the booking's comp flag, so this sends no
 *  `method`. No money moves and the customer is not emailed. */
export async function cancelBooking(id: string, reason: string): Promise<CancelBookingResult> {
  const res = await apiFetch(`/api/v2/bookings/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) throw await cancelBookingErrorFromResponse(res);
  return (await res.json()) as CancelBookingResult;
}
